import React, { type MouseEvent, useRef, useState } from 'react';
import styles from './Resizer.module.css';
import classnames from 'classnames';
import useEventHandler from '../useEventHandler';

export type DragHandlePosition =
  | 'n'
  | 'e'
  | 's'
  | 'w'
  | 'ne'
  | 'se'
  | 'sw'
  | 'nw';

interface DragHandleProps {
  // Which side of the resizer the drag handle should be positioned.
  position: DragHandlePosition;
  // Callback when drag handle move starts.
  onMoveStart?: (position: DragHandlePosition) => void;
  // Callback when drag handle has moved.
  onMove?: (position: DragHandlePosition, x: number, y: number) => void;
  // Callback when drag handle has finished moving.
  onMoved?: (position: DragHandlePosition, x: number, y: number) => void;
}

const DragHandle = ({
  position,
  onMoveStart,
  onMove,
  onMoved,
}: DragHandleProps) => {
  const [dragging, setDragging] = useState(false);
  const lastMove = useRef<{ x: number; y: number }>();

  useEventHandler('mousemove', (event: MouseEvent) => onMouseMove(event));
  useEventHandler('mouseup', (event: MouseEvent) => onMouseUp(event));

  const onMouseDown = (event: MouseEvent) => {
    setDragging(true);

    if (onMoveStart) {
      onMoveStart(position);
    }

    event.stopPropagation();
  };

  const onMouseMove = (event: MouseEvent) => {
    if (dragging) {
      event.stopPropagation();
      event.preventDefault();

      const { clientX: x, clientY: y } = event;

      if (onMove) onMove(position, x, y);
      lastMove.current = { x, y };
    }
  };

  const onMouseUp = (event: MouseEvent) => {
    if (dragging) {
      setDragging(false);

      if (lastMove.current) {
        if (onMoved) {
          onMoved(position, lastMove.current.x, lastMove.current.y);
        }
        lastMove.current = undefined;
      }
    }
  };

  return (
    <span
      className={classnames({
        [styles.handle]: true,
        [styles.dragging]: dragging,
        [styles[`position${position}`]]: true,
      })}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      data-testid="resizer-drag-handle"
    />
  );
};

export default DragHandle;
